import { useState, useEffect, useRef } from "react";
import i18n from "@helpers/i18n";
import api from "@helpers/api";
import { getCookie } from "@helpers/cookies";
import { formatSSN, logUserAction } from "@helpers/utils";

// HOOKS
import useResponsive from "@hooks/useResponsive";

const useUserActivityView = props => {
  const {
    configOptions,
    user,
    // isLoadingCases,
    partner,
    advisor,
    caseFilterContext,
    setCustomerState,
    onWorkflowSwitched,
    nextStepOptions
  } = props;

  const isMobileOrTablet = useResponsive("down", "md");
  const [mounted, setMounted] = useState(false);
  const [activityLogsLoaded, setActivityLogsLoaded] = useState(false);
  const [currentlyLoggedLoaded, setCurrentlyLoggedLoaded] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [currentlyLogged, setCurrentlyLogged] = useState([]);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [numCases, setNumCases] = useState(0);
  const [page, setPage] = useState(0);
  // const searchTimeout = useRef(0);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("Created");
  const [filters, setFilters] = useState({
    user: null,
    client: null,
    search: ""
  });

  // check if we have any options saved..
  const cobOptions = JSON.parse(getCookie("cobOptions")) || null;
  // .. and set rowsPerPage setting accordingly
  const [rowsPerPage, setRowsPerPage] = useState(
    cobOptions?.rowsPerPage["userLogs"] || 10
  );

  const { minActivityLogTime } = configOptions;

  let allActivityColumns = [];
  allActivityColumns.push(
    {
      Header: `${i18n("tables", "time")}`,
      className: "Created",
      accessor: "Created",
      width: "10%"
    },
    {
      Header: `${i18n("tables", "user")}`,
      className: "UserFirstName",
      accessor: "UserFirstName",
      width: "30%"
    },
    {
      Header: `${i18n("tables", "activity")}`,
      className: "Action",
      accessor: "Action",
      width: "60%",
      disableSort: true
    }
  );

  let loggedNowColumns = [];
  loggedNowColumns.push(
    {
      Header: "",
      className: "Created",
      accessor: "Created",
      width: "10%",
      disableSort: true
    },
    {
      Header: `${i18n("tables", "user")}`,
      className: "UserFirstName",
      accessor: "UserFirstName",
      width: "30%",
      disableSort: true
    },
    {
      Header: `${i18n("tables", "latest_activity")}`,
      className: "Action",
      accessor: "Action",
      width: "60%",
      disableSort: true
    }
  );

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setPage(0);
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const toggleAddCustomerModal = action => {
    if (!customerModalOpen && (action === "add" || addCustomerModalOpen))
      setSelectedCustomer(null);

    if (addCustomerModalOpen) {
      // disable/enable background content scrolling while modal is open
      document.documentElement.style.overflow = "auto";
    } else {
      document.documentElement.style.overflow = "hidden";
    }

    setAddCustomerModalOpen(!addCustomerModalOpen);
  };

  const handleClientClick = ssn => async e => {
    e.preventDefault();

    const { client } = await api("/client/relationship", {
      ssn: formatSSN(ssn, { withDash: true })
    });

    setCustomerState(
      {
        isCompany: false,
        ssnOrg: client.id ? client.id.replace("-", "") : "",
        isNewCase: null,
        standaloneApplicationsChecked: [],
        needsNewDocumentation: false,
        kycCaseId: null,
        kycInfoLoading: false,
        overrideNewDocumentation: false,
        casesLoading: false,
        cases: null,
        selectedCaseId: null,
        selectedCaseIndex: -1,
        customerModalIsOpen: true
      },
      () => {
        setCustomerModalOpen(true);
        setSelectedCustomer(client);
        document.documentElement.style.overflow = "hidden";
      }
    );
  };

  const handleClientModalClose = () => {
    setCustomerState(
      {
        isCompany: false,
        ssnOrg: "",
        customerModalIsOpen: false
      },
      () => {
        setCustomerModalOpen(false);
        setSelectedCustomer(null);
      }
    );
  };

  const refreshLogs = async () => {
    fetchLogs(filters, page, order, orderBy);

    const { client } = await api("/client/relationship", {
      ssn: selectedCustomer.id
    });
    setSelectedCustomer(client);
  };

  const fetchLogs = async (filters, page, order, orderBy) => {

    const requests = [
      {
        id: "userLogs",
        promise: api("/partner/user/logs", {
          // ssn: ssn,
          filters,
          page: page || 0,
          order: order || "desc",
          orderBy: orderBy || "created",
          limit: rowsPerPage === -1 ? 100 : rowsPerPage
        })
      },
      {
        id: "loggedInUsers",
        promise: api("/partner/user/logs/loggedin", {
          minActivityLogTime
        })
      }
    ];

  // Create an array of just the promises
    const promises = requests.map(req => req.promise);

    setActivityLogsLoaded(false);
    Promise.all(promises).then(responses => {
      setActivityLogs(responses[0].batch);
      setUsers(responses[0].users);
      setClients(responses[0].clients);
      setNumCases(parseInt(responses[0].total));

      const currentlyLoggedUsers = responses[1];
      if (!mounted) {
        // Filter currentlyLogged so we only list each user latest activity
        const uniqueUsers = Object.values(
          currentlyLoggedUsers.reduce((acc, log) => {
            if (
              !acc[log.UserId] ||
              new Date(acc[log.UserId].Created) < new Date(log.Created)
            ) {
              acc[log.UserId] = log;
            }
            return acc;
          }, {})
        );
  
        setCurrentlyLogged(uniqueUsers);
        setCurrentlyLoggedLoaded(true);
      }

      setActivityLogsLoaded(true);  
    });
  };

  // inital load and page/rowsPerPage changes useEffect
  useEffect(() => {
    fetchLogs(filters, page, order, orderBy);
    if (!mounted) {
      logUserAction({
        action: "NAV_USER_ACTIVITY_VIEW"
      });
      setMounted(true);
    }
  }, [filters, page, rowsPerPage, order, orderBy]);

  // // Search useEffect
  // useEffect(() => {
  //   if (mounted) {
  //     if (searchTimeout.current > 0) clearTimeout(searchTimeout.current);
  //     searchTimeout.current = setTimeout(() => {
  //       if (page > 0) setPage(0);
  //       fetchLogs(filters, page, order, orderBy);
  //     }, 2000);
  //   }
  // }, [filters.search]);

  // // CASES loading useEffect
  // useEffect(() => {
  //   if (mounted) {
  //     setActivityLogsLoaded(!isLoadingCases);
  //   }
  // }, [isLoadingCases]);

  return {
    isMobileOrTablet,
    user,
    partner,
    advisor,
    activityLogsLoaded,
    currentlyLoggedLoaded,
    allActivityColumns,
    loggedNowColumns,
    minActivityLogTime,
    activityLogs,
    currentlyLogged,
    users,
    clients,
    numCases,
    page,
    setPage,
    filters,
    setFilters,
    order,
    orderBy,
    rowsPerPage,
    selectedCustomer,
    customerModalOpen,
    addCustomerModalOpen,
    setRowsPerPage,
    setSelectedCustomer,
    handleChangePage,
    handleRequestSort,
    toggleAddCustomerModal,
    handleClientClick,
    handleClientModalClose,
    refreshLogs,
    caseFilterContext,
    setCustomerState,
    onWorkflowSwitched,
    nextStepOptions
  };
};

export default useUserActivityView;
